import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { ASSISTANT_CHAT_BUSY, ASSISTANT_RUN_POLL_MS, ASSISTANT_RUN_TEXT, type AssistantAttachmentView, type AssistantRunState } from '@repo/types'
import { api } from '@/lib/api'
import { describeApiError } from '@/lib/api-error'
import { getMe } from '@/lib/auth'
import { useAuth } from '@/contexts/AuthContext'
import { ASSISTANT_CONNECTION_LOST, streamAssistantChat, type SseEvent } from '@/lib/assistant-stream'
import { dealKeys } from '@/hooks/api/deal-keys'
import { eventKeys } from '@/hooks/api/events'
import { taskKeys } from '@/hooks/api/tasks'
import { contactKeys } from '@/hooks/api/contacts'
import { propertyKeys } from '@/hooks/api/properties'
import { emailKeys } from '@/hooks/api/emails'
import { callLogKeys } from '@/hooks/api/call-logs'
import { dashboardKeys } from '@/hooks/api/dashboard'
import { reportKeys } from '@/hooks/api/reports'
import { ASSISTANT_CHAT_ID_KEY } from './assistant-roles'

export interface ChatBubble {
  id: string
  role: 'user' | 'assistant'
  text: string
  isError?: boolean
  attachments?: AssistantAttachmentView[]
}

export interface EmailDraft {
  toolUseId: string
  draft: { accountId: string; to: string[]; cc?: string[]; subject: string; bodyHtml: string }
}

/**
 * Zápisové nástroje → prefixy query kľúčov, ktoré treba po odpovedi obnoviť
 * (otvorená stránka ukáže zmenu). Obchody: zoznam ['my-deals'], pipeline ['pipeline'],
 * detail ['deal', id], dashboard ['dashboard-stats'], reporty ['deal-stats'],
 * changelog štádií (Môj tím) ['deal-stage-changelog'], rozdelenie provízie
 * ['commission-splits'] (uzavretie ho zakladá). Nehnuteľnosti: koncové štádium ju označí SOLD,
 * strata/obnova vráti ACTIVE → zoznam ['properties'], detail ['property'], história
 * ['property-history']. E-mail: odoslaná správa → zoznamy ['email-messages'] a komunikácia
 * pri kontakte/obchode ['comm-emails']. Úlohy: ['tasks'] kryje aj
 * kalendárové úlohy (['tasks', 'calendar', …]). Udalosti: ['events'] kryje aj históriu
 * udalostí pri entitách. Úlohy aj udalosti sú navyše v detaile obchodu a na dashboarde
 * (rovnako ako v hookoch tasks.ts / events.ts). Kontakty = jedna osoba CRM pod ['contacts'].
 */
const DEAL_KEYS: QueryKey[] = [
  dealKeys.lists, dealKeys.pipeline, dealKeys.details, dashboardKeys.stats, ['deal-stats'], ['deal-stage-changelog'],
  ['commission-splits'], propertyKeys.lists, ['property'], ['property-history'],
]
const WRITE_TOOL_KEYS: Record<string, QueryKey[]> = {
  move_deal_stage: DEAL_KEYS,
  mark_deal_lost: DEAL_KEYS,
  restore_deal_lost: DEAL_KEYS,
  create_event: [eventKeys.all, dealKeys.details, dashboardKeys.stats],
  update_event: [eventKeys.all, dealKeys.details, dashboardKeys.stats],
  update_event_note: [eventKeys.all, dealKeys.details],
  create_task: [taskKeys.all, dealKeys.details, dashboardKeys.stats],
  update_task: [taskKeys.all, dealKeys.details, dashboardKeys.stats],
  // Nová osoba mení aj KPI Reportov a počty dashboardu (rovnako ako invalidateContactViews).
  create_contact: [contactKeys.all, reportKeys.all, dashboardKeys.stats],
  // Poznámka linkovaného hovoru je poznámka kontaktu — obnoviť aj jeho detail.
  update_call_note: [callLogKeys.all, contactKeys.all],
  send_email: [emailKeys.messages, ['comm-emails']],
  // Dokument nehnuteľnosti je médium typu DOCUMENT, dokument obchodu je v jeho detaile.
  save_attachment: [dealKeys.details, ['property'], ['property-media'], ['property-history']],
}

/**
 * Kľúče na obnovu po ťahu asistenta. `null` = nástroje ťahu klient nepozná (odpoveď dobehla bez
 * spojenia) → všetky. Ťah mohol aj založiť chat alebo zmeniť jeho titulok/poradie v histórii.
 */
function keysAfterRun(toolNames: Iterable<string> | null): QueryKey[] {
  const keys = [...(toolNames ?? Object.keys(WRITE_TOOL_KEYS))].flatMap((name) => WRITE_TOOL_KEYS[name] ?? [])
  keys.push(['assistant-chats'])
  // Viac nástrojov zdieľa kľúče — každý stačí obnoviť raz.
  return [...new Map(keys.map((key) => [JSON.stringify(key), key])).values()]
}

const readStoredChatId = (): string | undefined => {
  try { return localStorage.getItem(ASSISTANT_CHAT_ID_KEY) ?? undefined } catch { return undefined }
}
const storeChatId = (id: string | undefined) => {
  try {
    if (id) localStorage.setItem(ASSISTANT_CHAT_ID_KEY, id)
    else localStorage.removeItem(ASSISTANT_CHAT_ID_KEY)
  } catch { /* private mode / zablokované úložisko */ }
}

const FALLBACK_ERROR = 'Neočakávaná chyba asistenta.'
const LOAD_ERROR = 'Chat sa nepodarilo načítať.'

function isEmailDraft(draft: unknown): draft is EmailDraft['draft'] {
  if (!draft || typeof draft !== 'object') return false
  const d = draft as Record<string, unknown>
  // EmailConfirmCard renderuje priamo to/cc (join), subject a bodyHtml.
  return (
    Array.isArray(d.to) &&
    (d.cc === undefined || Array.isArray(d.cc)) &&
    typeof d.subject === 'string' &&
    typeof d.bodyHtml === 'string' &&
    (d.accountId === undefined || typeof d.accountId === 'string')
  )
}

let seq = 0
const nextId = () => `a${++seq}`

/**
 * Uložená história (GET /assistant/chats/:id → `toDisplayMessages` na serveri) → bubliny.
 * Tvar: `{ role: 'user', content: string, attachments? }` | `{ role: 'assistant', content: [{ type: 'text', text }] }`.
 */
function toBubbles(messages: unknown[]): ChatBubble[] {
  const out: ChatBubble[] = []
  for (const m of messages as Array<{ role?: string; content?: unknown; attachments?: unknown }>) {
    if (m?.role === 'user' && typeof m.content === 'string') {
      const attachments = Array.isArray(m.attachments) && m.attachments.length > 0 ? (m.attachments as AssistantAttachmentView[]) : undefined
      out.push({ id: nextId(), role: 'user', text: m.content, ...(attachments && { attachments }) })
    } else if (m?.role === 'assistant' && Array.isArray(m.content)) {
      const text = (m.content as Array<{ type?: string; text?: string }>)
        .filter((b) => b?.type === 'text' && typeof b.text === 'string')
        .map((b) => b.text)
        .join('')
      if (text) out.push({ id: nextId(), role: 'assistant', text })
    }
  }
  return out
}

/** Bublina k stavu behu po načítaní chatu; `null` = stav sa ukáže inak (indikátor, karta e-mailu). */
function runNotice(run: AssistantRunState): ChatBubble | null {
  switch (run.status) {
    case 'interrupted':
      return { id: nextId(), role: 'assistant', text: ASSISTANT_RUN_TEXT.interrupted, isError: true }
    case 'waiting_device':
      return { id: nextId(), role: 'assistant', text: ASSISTANT_RUN_TEXT.waitingDeviceElsewhere }
    default:
      return null
  }
}

/** Text novej správy, ktorý sa vráti do poľa, ak ju server neprijal. */
interface UnsentMessage {
  text: string
  restore: (text: string) => void
}

/** Posledná user správa v histórii servera — či server správu, ktorej prijatie nepotvrdil, predsa uložil. */
function lastUserText(data: any): string | undefined {
  const users = toBubbles(Array.isArray(data?.messages) ? data.messages : []).filter((b) => b.role === 'user')
  return users[users.length - 1]?.text
}

/** Prílohy sa porovnávajú podľa ID — URL je presigned a každé načítanie chatu ju vráti inú. */
const sameAttachments = (a: AssistantAttachmentView[] = [], b: AssistantAttachmentView[] = []) =>
  a.length === b.length && a.every((att, i) => att.id === b[i].id)

/**
 * Polling vracia celú históriu s novými ID bublín aj URL príloh. Nezmenené bubliny ostávajú tie isté
 * objekty, aby ich React neprekreslil (obrázky sa znova nesťahujú, výber textu ostane).
 */
function mergeBubbles(prev: ChatBubble[], next: ChatBubble[]): ChatBubble[] {
  let changed = prev.length !== next.length
  const merged = next.map((bubble, i) => {
    const old = prev[i]
    if (
      old &&
      old.role === bubble.role &&
      old.text === bubble.text &&
      !!old.isError === !!bubble.isError &&
      sameAttachments(old.attachments, bubble.attachments)
    ) {
      return old
    }
    changed = true
    return bubble
  })
  return changed ? merged : prev
}

export interface AssistantContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  bubbles: ChatBubble[]
  /** Popisok práve bežiaceho nástroja (SSE `tool` → `label`), inak null. */
  activity: string | null
  pendingEmail: EmailDraft | null
  isStreaming: boolean
  /** Načítava sa uložený chat (loadChat / obnova po reloade) — send/respondToEmail sa vtedy ignorujú. */
  isLoadingChat: boolean
  chatId: string | undefined
  /**
   * Text aj prílohy sú nepovinné — prázdna správa (bez oboch) sa ignoruje. `onUnsent` vráti text
   * do poľa, ak ho server neprijal: CHAT_BUSY (v chate ešte beží predchádzajúca odpoveď) alebo spojenie
   * padlo skôr, než prišla udalosť `chat`.
   */
  send: (text: string, attachments?: AssistantAttachmentView[], onUnsent?: (text: string) => void) => void
  respondToEmail: (toolUseId: string, approved: boolean) => void
  /**
   * Nový chat — UI sa odpojí od bežiacej odpovede a zabudne chatId. Spojenie ostane otvorené (bez pushu
   * na telefón, pozri cancelCurrent), server odpoveď dokončí do Histórie.
   */
  reset: () => void
  /**
   * Otvorí uložený chat (z histórie). Neexistujúci/cudzí chat (404) → prázdny nový chat;
   * iná chyba → chybová bublina, uložené chatId ostáva.
   */
  loadChat: (id: string) => Promise<void>
}

const AssistantContext = createContext<AssistantContextValue | null>(null)

export function AssistantProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { setUser } = useAuth()
  const [open, setOpen] = useState(false)
  const [bubbles, setBubbles] = useState<ChatBubble[]>([])
  const [activity, setActivity] = useState<string | null>(null)
  const [pendingEmail, setPendingEmail] = useState<EmailDraft | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isLoadingChat, setIsLoadingChat] = useState(false)
  const [chatId, setChatIdState] = useState<string | undefined>(readStoredChatId)
  const chatIdRef = useRef(chatId)
  // Streamy aj tých behov, od ktorých sa UI odpojilo (prepnutie chatu) — zatvorí ich až unmount (odhlásenie).
  const streamsRef = useRef(new Set<AbortController>())
  const streamingRef = useRef(false)
  const loadingRef = useRef(false)
  const restoredRef = useRef(false)
  const restoreFailedRef = useRef(false)
  const bubblesRef = useRef(bubbles)
  bubblesRef.current = bubbles
  // Každá user akcia (run/reset/loadChat) zvýši generáciu — oneskorené výsledky starších akcií sa zahodia.
  const generationRef = useRef(0)
  // Odpoveď beží na serveri bez spojenia (reload, CHAT_BUSY) — kým je panel otvorený, pýta sa na stav.
  const [polling, setPolling] = useState(false)

  const setChatId = useCallback((id: string | undefined) => {
    chatIdRef.current = id
    setChatIdState(id)
    storeChatId(id)
  }, [])

  const setStreaming = useCallback((value: boolean) => {
    streamingRef.current = value
    setIsStreaming(value)
  }, [])

  const setLoading = useCallback((value: boolean) => {
    loadingRef.current = value
    setIsLoadingChat(value)
  }, [])

  /**
   * Odpojí UI od bežiacej akcie: vyššia generácia zahodí udalosti a výsledky starších akcií, zastaví sa
   * načítanie aj polling; stav si nastaví volajúci. Stream sa zámerne nezatvára — server by zatvorené
   * spojenie bral ako odchod používateľa a na konci poslal push na telefón. Odpoveď dobehne do Histórie.
   */
  const cancelCurrent = useCallback(() => {
    generationRef.current++
    setLoading(false)
    setPolling(false)
  }, [setLoading])

  /** `newBubble` = delta začína novú správu asistenta (server každú iteráciu po nástroji ukladá zvlášť). */
  const appendDelta = useCallback((delta: string, newBubble: boolean) => {
    setBubbles((prev) => {
      const last = prev[prev.length - 1]
      if (!newBubble && last?.role === 'assistant' && !last.isError) {
        return [...prev.slice(0, -1), { ...last, text: last.text + delta }]
      }
      return [...prev, { id: nextId(), role: 'assistant', text: delta }]
    })
  }, [])

  const invalidateAfterRun = useCallback((toolNames: Iterable<string> | null) => {
    for (const queryKey of keysAfterRun(toolNames)) void queryClient.invalidateQueries({ queryKey })
  }, [queryClient])

  /** Bubliny a stav behu z GET /assistant/chats/:id. */
  const applyChat = useCallback((data: any, id: string) => {
    setChatId(data?.id ?? id)
    const restored = toBubbles(Array.isArray(data?.messages) ? data.messages : [])
    const runState: AssistantRunState = data?.run ?? { status: 'idle' }
    const notice = runNotice(runState)
    setBubbles((prev) => mergeBubbles(prev, notice ? [...restored, notice] : restored))
    setPendingEmail(
      runState.status === 'waiting_email' && isEmailDraft(runState.draft) ? { toolUseId: runState.toolUseId, draft: runState.draft } : null,
    )
    const running = runState.status === 'running'
    setStreaming(running)
    setActivity(running ? ASSISTANT_RUN_TEXT.running : null)
    setPolling(running)
  }, [setChatId, setStreaming])

  /**
   * Vráti false pri inej chybe než 404 (chat ostáva uložený, dá sa skúsiť znova). `lost` = načítanie po
   * strate spojenia: pri chybe ostanú zobrazené bubliny a pribudne chybová s `lost.message`; `lost.unsent`
   * = správa, ktorej prijatie server nepotvrdil (udalosť `chat` neprišla).
   */
  const loadChatInner = useCallback(async (id: string, lost?: { message: string; unsent?: UnsentMessage }): Promise<boolean> => {
    cancelCurrent()
    const generation = generationRef.current
    setLoading(true)
    setStreaming(false)
    setActivity(null)
    setPendingEmail(null)
    const failText = lost?.message
    try {
      // 404 (zmazaný chat) ticho, inú chybu ukáže chybová bublina (`lost.message`, inak LOAD_ERROR).
      const { data } = await api.get(`/assistant/chats/${id}`, { skipErrorToast: true })
      if (generation !== generationRef.current) return true
      applyChat(data, id)
      if (lost?.unsent && lastUserText(data) !== lost.unsent.text) {
        // Spojenie padlo skôr, než server správu prijal — bez textu v poli a chyby by zmizla bez stopy.
        lost.unsent.restore(lost.unsent.text)
        const errorBubble: ChatBubble = { id: nextId(), role: 'assistant', text: lost.message, isError: true }
        setBubbles((prev) => [...prev, errorBubble])
      }
      return true
    } catch (e) {
      if (generation !== generationRef.current) return true
      if (isAxiosError(e) && e.response?.status === 404) {
        // zmazaný/cudzí chat — začni nanovo
        setChatId(undefined)
        setBubbles([])
        return true
      }
      setChatId(id)
      // Po strate spojenia nesmie chyba zmazať rozpísaný ťah (rovnako ako mobil).
      const errorBubble: ChatBubble = { id: nextId(), role: 'assistant', text: failText ?? LOAD_ERROR, isError: true }
      setBubbles((prev) => (failText ? [...prev, errorBubble] : [errorBubble]))
      return false
    } finally {
      if (generation === generationRef.current) setLoading(false)
    }
  }, [applyChat, cancelCurrent, setChatId, setLoading, setStreaming])

  /** `unsent` má len nová správa s textom — pokračovanie po e-maile do poľa nič nevracia. */
  const run = useCallback(async (payload: object, unsent?: UnsentMessage) => {
    cancelCurrent()
    const generation = generationRef.current
    // Po prepnutí chatu stream dobieha len kvôli serveru — nesmie meniť UI nového chatu.
    const isCurrent = () => generation === generationRef.current
    const controller = new AbortController()
    streamsRef.current.add(controller)
    setStreaming(true)
    setActivity(null)
    setPendingEmail(null)
    const touched = new Set<string>()
    // Po nástroji ide text do novej bubliny — rovnako ako po reloade (každá iterácia = samostatná správa).
    // Štart na true: nový ťah (aj pokračovanie po zamietnutí e-mailu, ktoré `tool` event nepošle)
    // je vždy nová správa asistenta.
    let breakNext = true
    // Objekt, nie `let`: TS by `let` priradené len v callbacku zúžil na null a kontrola za streamom by bola `never`.
    const followUp: { code: string | null; message?: string; accepted: boolean } = { code: null, accepted: false }

    // streamAssistantChat nikdy nezlyhá — každá chyba príde ako jeden `error` event,
    // zrušenie (abort pri unmounte) je tiché.
    const onEvent = ({ event, data }: SseEvent) => {
      // Nástroje sa zbierajú aj po prepnutí chatu — invalidácia po dobehnutí má pokryť celý beh.
      if (event === 'tool' && typeof data?.name === 'string') touched.add(data.name)
      if (!isCurrent()) return
      if (typeof data?.chatId === 'string') setChatId(data.chatId)
      switch (event) {
        case 'chat':
          // Server správu prijal (CHAT_BUSY už nepríde, pri strate spojenia je v jeho histórii).
          followUp.accepted = true
          break
        case 'text':
          setActivity(null)
          if (typeof data?.delta === 'string' && data.delta) {
            appendDelta(data.delta, breakNext)
            breakNext = false
          }
          break
        case 'tool':
          breakNext = true
          setActivity(typeof data?.label === 'string' ? data.label : null)
          break
        case 'confirmation_required':
          breakNext = true
          setActivity(null)
          if (typeof data?.toolUseId === 'string' && isEmailDraft(data?.draft)) {
            setPendingEmail({ toolUseId: data.toolUseId, draft: data.draft })
          } else {
            setBubbles((prev) => [...prev, { id: nextId(), role: 'assistant', text: 'Návrh e-mailu sa nepodarilo zobraziť.', isError: true }])
          }
          break
        case 'error':
          if (data?.code === ASSISTANT_CHAT_BUSY || (data?.code === ASSISTANT_CONNECTION_LOST && chatIdRef.current)) {
            // Odpoveď beží na serveri (iná karta/zariadenie, alebo spadlo spojenie) — namiesto chyby jej stav.
            followUp.code = data.code
            followUp.message = typeof data?.message === 'string' ? data.message : FALLBACK_ERROR
            break
          }
          setBubbles((prev) => [
            ...prev,
            { id: nextId(), role: 'assistant', text: typeof data?.message === 'string' ? data.message : FALLBACK_ERROR, isError: true },
          ])
          // Súhlas bol odvolaný / asistent vypnutý medzičasom (iná karta, Môj tím) — obnov usera,
          // aby sa zobrazila súhlasová brána, resp. panel zmizol (AssistantPanel číta user).
          if (data?.code === 'AI_CONSENT_REQUIRED' || data?.code === 'AI_ASSISTANT_DISABLED') {
            void getMe().then(setUser).catch(() => {})
          }
          break
      }
    }

    try {
      await streamAssistantChat(payload, onEvent, controller.signal)
    } finally {
      streamsRef.current.delete(controller)
      if (isCurrent()) {
        setStreaming(false)
        setActivity(null)
      }
      // Aj po prepnutí chatu — zápis na serveri prebehol; obnova zoznamov stav chatu nemení.
      invalidateAfterRun(touched)
    }
    const resumeId = chatIdRef.current
    if (!followUp.code || !resumeId || !isCurrent()) return
    if (followUp.code === ASSISTANT_CHAT_BUSY) {
      unsent?.restore(unsent.text)
      void loadChatInner(resumeId)
    } else {
      void loadChatInner(resumeId, { message: followUp.message ?? FALLBACK_ERROR, unsent: followUp.accepted ? undefined : unsent })
    }
  }, [appendDelta, cancelCurrent, invalidateAfterRun, loadChatInner, setChatId, setStreaming])

  const send = useCallback((text: string, attachments: AssistantAttachmentView[] = [], onUnsent?: (text: string) => void) => {
    const trimmed = text.trim()
    if ((!trimmed && attachments.length === 0) || streamingRef.current || loadingRef.current) return
    setBubbles((prev) => [...prev, { id: nextId(), role: 'user', text: trimmed, ...(attachments.length > 0 && { attachments }) }])
    void run(
      {
        ...(trimmed && { message: trimmed }),
        ...(attachments.length > 0 && { attachmentIds: attachments.map((a) => a.id) }),
        chatId: chatIdRef.current,
      },
      trimmed && onUnsent ? { text: trimmed, restore: onUnsent } : undefined,
    )
  }, [run])

  const respondToEmail = useCallback((toolUseId: string, approved: boolean) => {
    if (streamingRef.current || loadingRef.current) return
    setPendingEmail(null)
    void run({ approval: { toolUseId, approved }, chatId: chatIdRef.current })
  }, [run])

  const reset = useCallback(() => {
    cancelCurrent()
    setChatId(undefined)
    setBubbles([])
    setPendingEmail(null)
    setActivity(null)
    setStreaming(false)
  }, [cancelCurrent, setChatId, setStreaming])

  const loadChat = useCallback(async (id: string) => {
    await loadChatInner(id)
  }, [loadChatInner])

  // Po reloade stránky obnov rozpracovaný chat pri prvom otvorení panelu. Pri zlyhaní
  // (nie 404) sa obnova skúsi znova až pri ďalšom otvorení — nie pri každom renderi.
  useEffect(() => {
    if (!open || restoredRef.current) return
    restoredRef.current = true
    const id = chatIdRef.current
    if (id && bubblesRef.current.every((b) => b.isError)) {
      void loadChatInner(id).then((ok) => {
        if (!ok) restoreFailedRef.current = true
      })
    }
  }, [open, loadChatInner])

  useEffect(() => {
    if (!polling || !open) return
    const generation = generationRef.current
    let timer: ReturnType<typeof setTimeout>
    let stopped = false
    const tick = async () => {
      const id = chatIdRef.current
      if (id) {
        try {
          const { data } = await api.get(`/assistant/chats/${id}`, { skipErrorToast: true })
          if (stopped || generation !== generationRef.current) return
          applyChat(data, id)
          // Odpoveď dobehla bez spojenia — klient nevie, ktoré nástroje bežali, obnoví všetko.
          if (data?.run?.status !== 'running') invalidateAfterRun(null)
        } catch (e) {
          if (!stopped && isAxiosError(e) && e.response?.status === 404) {
            // Chat zmazaný inde — loadChatInner ho zahodí a cez cancelCurrent zastaví polling.
            stopped = true
            void loadChatInner(id)
            return
          }
          const status = isAxiosError(e) ? e.response?.status : undefined
          if (!stopped && isAxiosError(e) && status !== undefined && status >= 400 && status < 500 && status !== 429) {
            // 403 (asistent vypnutý, súhlas odvolaný) či 400 sa opakovaním nezmení — bez zastavenia by
            // panel donekonečna ukazoval „Asistent pracuje…".
            stopped = true
            setPolling(false)
            setStreaming(false)
            setActivity(null)
            const text = describeApiError(status, e.response?.data, LOAD_ERROR)
            setBubbles((prev) => [...prev, { id: nextId(), role: 'assistant', text, isError: true }])
            return
          }
          // výpadok siete, 5xx a 429 — ďalší pokus o ASSISTANT_RUN_POLL_MS
        }
      }
      if (!stopped) timer = setTimeout(tick, ASSISTANT_RUN_POLL_MS)
    }
    timer = setTimeout(tick, ASSISTANT_RUN_POLL_MS)
    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [polling, open, applyChat, invalidateAfterRun, loadChatInner, setStreaming])

  useEffect(() => {
    if (!open && restoreFailedRef.current) {
      restoreFailedRef.current = false
      restoredRef.current = false
    }
  }, [open])

  // Unmount = odhlásenie: tam je zatvorenie spojení (a push na telefón po dobehnutí) správne.
  useEffect(() => {
    const streams = streamsRef.current
    return () => streams.forEach((controller) => controller.abort())
  }, [])

  const value = useMemo<AssistantContextValue>(() => ({
    open, setOpen, bubbles, activity, pendingEmail, isStreaming, isLoadingChat, chatId, send, respondToEmail, reset, loadChat,
  }), [open, bubbles, activity, pendingEmail, isStreaming, isLoadingChat, chatId, send, respondToEmail, reset, loadChat])

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>
}

export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext)
  if (!ctx) throw new Error('useAssistant must be used within AssistantProvider')
  return ctx
}
